import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ToastService } from '@horizon/core-notifications';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable } from 'rxjs';

interface ErrorResponse {
  severity: string;
  type: string;
  message: string;
  fields: { [key: string]: string };
}

interface Error {
  type: 'error' | 'warning';
  text: string;
}

@Injectable({
  providedIn: 'root',
})
export class ApiExceptionsHandlerService {
  constructor(
    private translate: TranslateService,
    private readonly toastService: ToastService
  ) {}

  public formatErrorMessage(response: ErrorResponse[], template?: any): any[] {
    if (response?.length) {
      return response.map((err: ErrorResponse) => {
        const type = err.severity.toLowerCase();

        if (err.message) {
          return { type, text: err.message };
        }

        if (err.type !== 'CustomRuleFailed') {
          return {
            type,
            text: this.translate.instant(
              `errors.${err.type}`,
              err.fields ? this.fieldsToLowerCase(err.fields) : {}
            ),
          };
        }

        if (!template) {
          return {
            type,
            text: this.translate.instant(
              `errors.${err.type}`,
              err.fields ? this.fieldsToLowerCase(err.fields) : {}
            ),
          };
        }
        const rules = template.fields.find(
          (el) => el.name === err.fields.fieldName
        ).rules;
        const errorMessage = rules.find(
          (el) => el.condition === err.fields.formula
        )?.errorMessage;

        if (errorMessage) {
          return {
            type,
            text:
              err.fields.fieldName +
              ': ' +
              errorMessage[this.translate.currentLang],
          };
        }

        return {
          type,
          text: this.translate.instant(
            `errors.${err.type}`,
            err.fields ? this.fieldsToLowerCase(err.fields) : {}
          ),
        };
      });
    }

    if (
      response.hasOwnProperty('type') ||
      response.hasOwnProperty('severity')
    ) {
      return [{ text: 'errors.generic', type: 'error' }];
    }
    return [];
  }

  public defaultErrorToasts(
    response: HttpErrorResponse,
    api: string,
    autoclose = false
  ): void {
    switch (response.status) {
      case 400:
        const errors = this.formatErrorMessage(response.error);
        errors.map((err) => {
          this.toastService.showToastNotification({
            ...err,
            autoclose,
          });
        });
        break;
      case 404:
        this.toastService.showToastNotification({
          text: this.translate.instant('errors.generic'),
          type: 'error',
          autoclose,
        });
        break;
      case 500:
        this.toastService.showToastNotification({
          text: this.translate.instant('errors.not_available', { api }),
          type: 'error',
          autoclose,
        });
        break;
      default:
        this.toastService.showToastNotification({
          text: 'errors.generic',
          type: 'error',
          autoclose,
        });
        break;
    }
  }

  public defaultErrorItems(
    response: HttpErrorResponse,
    api: string,
    template?: any
  ): Error[] {
    switch (response.status) {
      case 400:
        return this.formatErrorMessage(response?.error, template);
      case 403:
        return this.formatErrorMessage(response?.error);
      case 404:
        return [
          { text: this.translate.instant('errors.generic'), type: 'error' },
        ];

      case 500:
        if (response?.error && Array.isArray(response.error)) {
          return this.formatErrorMessage(response.error, template);
        } else {
          return [
            {
              text: this.translate.instant('errors.not_available', { api }),
              type: 'error',
            },
          ];
        }

      default:
        return [{ text: 'errors.generic', type: 'error' }];
    }
  }

  public fieldsToLowerCase(fields): Object {
    fields = Object.keys(fields).reduce((acc, key) => {
      const newKey = key.charAt(0).toLowerCase() + key.slice(1);
      acc[newKey] = fields[key];
      return acc;
    }, {} as any);

    return fields;
  }

  catchErrorHandler(
    error: HttpErrorResponse,
    api: string,
    autoclose = false
  ): Observable<never> {
    this.defaultErrorToasts(error, api, autoclose);
    return EMPTY;
  }
}
